package linkerpangoquipdb

import app.cash.sqldelight.Query
import app.cash.sqldelight.SuspendingTransacterImpl
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlCursor
import app.cash.sqldelight.db.SqlDriver
import kotlin.Any
import kotlin.Double
import kotlin.Int
import kotlin.Long
import kotlin.String

public class GarageQueries(
  driver: SqlDriver,
  private val vehicleAdapter: Vehicle.Adapter,
) : SuspendingTransacterImpl(driver) {
  public fun <T : Any> select(vin: String, mapper: (
    vin: String,
    plate: String,
    brand: String,
    model: String,
    vehicle_type: String,
    year: Long,
    color: String,
    owner: String,
    engine_oil: String,
    front_tire_size: String,
    rear_tire_size: String,
    front_tire_pressure_below_160: Double,
    rear_tire_pressure_below_160: Double,
    front_tire_pressure_above_160: Double,
    rear_tire_pressure_above_160: Double,
    recommended_fuel: String,
    engine_details: String,
    cylinder_count: Int,
    front_bulb_type: String,
    rear_bulb_type: String,
    battery_specs: String,
    coolant_type: String,
    signature: String?,
    next_revision_date: Long?,
    next_itv_date: Long?,
    first_registration_date: Long?,
  ) -> T): Query<T> = SelectQuery(vin) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getLong(5)!!,
      cursor.getString(6)!!,
      cursor.getString(7)!!,
      cursor.getString(8)!!,
      cursor.getString(9)!!,
      cursor.getString(10)!!,
      cursor.getDouble(11)!!,
      cursor.getDouble(12)!!,
      cursor.getDouble(13)!!,
      cursor.getDouble(14)!!,
      cursor.getString(15)!!,
      cursor.getString(16)!!,
      vehicleAdapter.cylinder_countAdapter.decode(cursor.getLong(17)!!),
      cursor.getString(18)!!,
      cursor.getString(19)!!,
      cursor.getString(20)!!,
      cursor.getString(21)!!,
      cursor.getString(22),
      cursor.getLong(23),
      cursor.getLong(24),
      cursor.getLong(25)
    )
  }

  public fun select(vin: String): Query<Vehicle> = select(vin, ::Vehicle)

  public fun <T : Any> select_all(mapper: (
    vin: String,
    plate: String,
    brand: String,
    model: String,
    vehicle_type: String,
    year: Long,
    color: String,
    owner: String,
    engine_oil: String,
    front_tire_size: String,
    rear_tire_size: String,
    front_tire_pressure_below_160: Double,
    rear_tire_pressure_below_160: Double,
    front_tire_pressure_above_160: Double,
    rear_tire_pressure_above_160: Double,
    recommended_fuel: String,
    engine_details: String,
    cylinder_count: Int,
    front_bulb_type: String,
    rear_bulb_type: String,
    battery_specs: String,
    coolant_type: String,
    signature: String?,
    next_revision_date: Long?,
    next_itv_date: Long?,
    first_registration_date: Long?,
  ) -> T): Query<T> = Query(-244_349_018, arrayOf("vehicle"), driver, "Garage.sq", "select_all", "SELECT vehicle.vin, vehicle.plate, vehicle.brand, vehicle.model, vehicle.vehicle_type, vehicle.year, vehicle.color, vehicle.owner, vehicle.engine_oil, vehicle.front_tire_size, vehicle.rear_tire_size, vehicle.front_tire_pressure_below_160, vehicle.rear_tire_pressure_below_160, vehicle.front_tire_pressure_above_160, vehicle.rear_tire_pressure_above_160, vehicle.recommended_fuel, vehicle.engine_details, vehicle.cylinder_count, vehicle.front_bulb_type, vehicle.rear_bulb_type, vehicle.battery_specs, vehicle.coolant_type, vehicle.signature, vehicle.next_revision_date, vehicle.next_itv_date, vehicle.first_registration_date FROM vehicle") { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getLong(5)!!,
      cursor.getString(6)!!,
      cursor.getString(7)!!,
      cursor.getString(8)!!,
      cursor.getString(9)!!,
      cursor.getString(10)!!,
      cursor.getDouble(11)!!,
      cursor.getDouble(12)!!,
      cursor.getDouble(13)!!,
      cursor.getDouble(14)!!,
      cursor.getString(15)!!,
      cursor.getString(16)!!,
      vehicleAdapter.cylinder_countAdapter.decode(cursor.getLong(17)!!),
      cursor.getString(18)!!,
      cursor.getString(19)!!,
      cursor.getString(20)!!,
      cursor.getString(21)!!,
      cursor.getString(22),
      cursor.getLong(23),
      cursor.getLong(24),
      cursor.getLong(25)
    )
  }

  public fun select_all(): Query<Vehicle> = select_all(::Vehicle)

  public fun select_distinct_owners(): Query<String> = Query(1_171_639_624, arrayOf("vehicle"), driver, "Garage.sq", "select_distinct_owners", "SELECT DISTINCT owner FROM vehicle WHERE owner IS NOT NULL AND owner != '' ORDER BY owner") { cursor ->
    cursor.getString(0)!!
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun insertVehicle(
    vin: String,
    plate: String,
    brand: String,
    model: String,
    vehicle_type: String,
    year: Long,
    color: String,
    owner: String,
    engine_oil: String,
    front_tire_size: String,
    rear_tire_size: String,
    front_tire_pressure_below_160: Double,
    rear_tire_pressure_below_160: Double,
    front_tire_pressure_above_160: Double,
    rear_tire_pressure_above_160: Double,
    recommended_fuel: String,
    engine_details: String,
    cylinder_count: Int,
    front_bulb_type: String,
    rear_bulb_type: String,
    battery_specs: String,
    coolant_type: String,
    signature: String?,
    next_revision_date: Long?,
    next_itv_date: Long?,
    first_registration_date: Long?,
  ): Long {
    val result = driver.execute(46_241_131, """
        |INSERT OR REPLACE INTO vehicle(vin, plate, brand, model, vehicle_type, year, color, owner, engine_oil, front_tire_size, rear_tire_size, front_tire_pressure_below_160, rear_tire_pressure_below_160, front_tire_pressure_above_160, rear_tire_pressure_above_160, recommended_fuel, engine_details, cylinder_count, front_bulb_type, rear_bulb_type, battery_specs, coolant_type, signature, next_revision_date, next_itv_date, first_registration_date)
        |VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
        """.trimMargin(), 26) {
          var parameterIndex = 0
          bindString(parameterIndex++, vin)
          bindString(parameterIndex++, plate)
          bindString(parameterIndex++, brand)
          bindString(parameterIndex++, model)
          bindString(parameterIndex++, vehicle_type)
          bindLong(parameterIndex++, year)
          bindString(parameterIndex++, color)
          bindString(parameterIndex++, owner)
          bindString(parameterIndex++, engine_oil)
          bindString(parameterIndex++, front_tire_size)
          bindString(parameterIndex++, rear_tire_size)
          bindDouble(parameterIndex++, front_tire_pressure_below_160)
          bindDouble(parameterIndex++, rear_tire_pressure_below_160)
          bindDouble(parameterIndex++, front_tire_pressure_above_160)
          bindDouble(parameterIndex++, rear_tire_pressure_above_160)
          bindString(parameterIndex++, recommended_fuel)
          bindString(parameterIndex++, engine_details)
          bindLong(parameterIndex++, vehicleAdapter.cylinder_countAdapter.encode(cylinder_count))
          bindString(parameterIndex++, front_bulb_type)
          bindString(parameterIndex++, rear_bulb_type)
          bindString(parameterIndex++, battery_specs)
          bindString(parameterIndex++, coolant_type)
          bindString(parameterIndex++, signature)
          bindLong(parameterIndex++, next_revision_date)
          bindLong(parameterIndex++, next_itv_date)
          bindLong(parameterIndex++, first_registration_date)
        }.await()
    notifyQueries(46_241_131) { emit ->
      emit("vehicle")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun update(
    brand: String,
    model: String,
    vehicle_type: String,
    year: Long,
    color: String,
    owner: String,
    plate: String,
    engine_oil: String,
    front_tire_size: String,
    rear_tire_size: String,
    front_tire_pressure_below_160: Double,
    rear_tire_pressure_below_160: Double,
    front_tire_pressure_above_160: Double,
    rear_tire_pressure_above_160: Double,
    recommended_fuel: String,
    engine_details: String,
    cylinder_count: Int,
    front_bulb_type: String,
    rear_bulb_type: String,
    battery_specs: String,
    coolant_type: String,
    signature: String?,
    next_revision_date: Long?,
    next_itv_date: Long?,
    first_registration_date: Long?,
    vin: String,
  ): Long {
    val result = driver.execute(-907_418_479, """UPDATE vehicle SET brand = ?, model = ?, vehicle_type = ?, year = ?, color = ?, owner = ?, plate = ?, engine_oil = ?, front_tire_size = ?, rear_tire_size = ?, front_tire_pressure_below_160 = ?, rear_tire_pressure_below_160 = ?, front_tire_pressure_above_160 = ?, rear_tire_pressure_above_160 = ?, recommended_fuel = ?, engine_details = ?, cylinder_count = ?, front_bulb_type = ?, rear_bulb_type = ?, battery_specs = ?, coolant_type = ?, signature = ?, next_revision_date = ?, next_itv_date = ?, first_registration_date = ? WHERE vin = ?""", 26) {
          var parameterIndex = 0
          bindString(parameterIndex++, brand)
          bindString(parameterIndex++, model)
          bindString(parameterIndex++, vehicle_type)
          bindLong(parameterIndex++, year)
          bindString(parameterIndex++, color)
          bindString(parameterIndex++, owner)
          bindString(parameterIndex++, plate)
          bindString(parameterIndex++, engine_oil)
          bindString(parameterIndex++, front_tire_size)
          bindString(parameterIndex++, rear_tire_size)
          bindDouble(parameterIndex++, front_tire_pressure_below_160)
          bindDouble(parameterIndex++, rear_tire_pressure_below_160)
          bindDouble(parameterIndex++, front_tire_pressure_above_160)
          bindDouble(parameterIndex++, rear_tire_pressure_above_160)
          bindString(parameterIndex++, recommended_fuel)
          bindString(parameterIndex++, engine_details)
          bindLong(parameterIndex++, vehicleAdapter.cylinder_countAdapter.encode(cylinder_count))
          bindString(parameterIndex++, front_bulb_type)
          bindString(parameterIndex++, rear_bulb_type)
          bindString(parameterIndex++, battery_specs)
          bindString(parameterIndex++, coolant_type)
          bindString(parameterIndex++, signature)
          bindLong(parameterIndex++, next_revision_date)
          bindLong(parameterIndex++, next_itv_date)
          bindLong(parameterIndex++, first_registration_date)
          bindString(parameterIndex++, vin)
        }.await()
    notifyQueries(-907_418_479) { emit ->
      emit("vehicle")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun delete_all(): Long {
    val result = driver.execute(-1_118_770_987, """DELETE FROM vehicle""", 0).await()
    notifyQueries(-1_118_770_987) { emit ->
      emit("maintenance")
      emit("vehicle")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun delete(vin: String): Long {
    val result = driver.execute(-1_404_030_605, """DELETE FROM vehicle WHERE vin = ?""", 1) {
          var parameterIndex = 0
          bindString(parameterIndex++, vin)
        }.await()
    notifyQueries(-1_404_030_605) { emit ->
      emit("maintenance")
      emit("vehicle")
    }
    return result
  }

  private inner class SelectQuery<out T : Any>(
    public val vin: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("vehicle", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("vehicle", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(-974_593_852, """SELECT vehicle.vin, vehicle.plate, vehicle.brand, vehicle.model, vehicle.vehicle_type, vehicle.year, vehicle.color, vehicle.owner, vehicle.engine_oil, vehicle.front_tire_size, vehicle.rear_tire_size, vehicle.front_tire_pressure_below_160, vehicle.rear_tire_pressure_below_160, vehicle.front_tire_pressure_above_160, vehicle.rear_tire_pressure_above_160, vehicle.recommended_fuel, vehicle.engine_details, vehicle.cylinder_count, vehicle.front_bulb_type, vehicle.rear_bulb_type, vehicle.battery_specs, vehicle.coolant_type, vehicle.signature, vehicle.next_revision_date, vehicle.next_itv_date, vehicle.first_registration_date FROM vehicle WHERE vin = ?""", mapper, 1) {
      var parameterIndex = 0
      bindString(parameterIndex++, vin)
    }

    override fun toString(): String = "Garage.sq:select"
  }
}
